import { useQuery, useMutation } from 'react-query'
import { axiosClient } from 'utils/axios-util'
import { useDynamicManageStore } from 'store/useDynamicManageStore'
import { useErrorStore } from 'store/useErrorStore'

import { LEASE, PDS, SURFACE_PROTECTION } from 'constants/pdsConstant'

const getFullName = ({ firstName = '', lastName = '' }) => {
    return `${firstName} ${lastName}`.toLowerCase()
}

const converToLowerCase = (name = '') => name.toLowerCase()

const getFullAddress = ({ addressLine1 = '', addressLine2 = '', city = '', state = '', zipCode = '' }) => {
    return [
        addressLine1.toLowerCase(),
        addressLine2.toLowerCase(),
        city.toLowerCase(),
        state.toUpperCase(),
        zipCode.slice(0, 5)
    ]
        .filter((item) => item)
        .join(', ')
}

const getSurfaceProtection = (dealerProducts = []) => {
    return dealerProducts.find((item) => item.productType === SURFACE_PROTECTION)?.amount || 0
}

const getTransactionType = ({ transactionType, vehicleFinancing }) => {
    if (transactionType === 'Retail' && !vehicleFinancing) {
        transactionType = 'Financing'
    } else if (transactionType === 'Retail' && vehicleFinancing.secondMonthlyPaymentsDue) {
        transactionType = 'FlexBuy'
    }
    return transactionType
}

const getDealerDiscount = (discount = 0) => -discount

const getPreTaxPrice = ({ preTaxPrice = 0, transactionType }) => (transactionType !== LEASE ? preTaxPrice : 0) // preTaxPrice not needed for lease

const getDealData = ({ dealJacketId, dealerId }) => {
    return axiosClient.get(`/${PDS}/${dealJacketId}/${dealerId}`)
}

const updateDealStatus = (status) => {
    const { dealJacketId, country, brand } = useDynamicManageStore.getState()
    const payload = {
        dealJacketId: dealJacketId,
        systemStatus: status,
        system: 'FMCC-PDS',
        country: country,
        brand: brand
    }
    return axiosClient.post(`/${PDS}/status`, payload)
}

const errorHandler = () => {
    const { showError } = useErrorStore.getState()
    showError({ backToDM: true })
}

export const useDealData = () => {
    const { dealJacketId, dealerId } = useDynamicManageStore((state) => state)
    return useQuery({
        queryKey: ['deal-info', dealJacketId, dealerId],
        queryFn: () => getDealData({ dealJacketId, dealerId }),
        onSuccess: (response) => {
            if (response) {
                response.data.totalCostOfAccessories = response.data.pdsAccessories.totalCostOfAccessories
                response.data.taxesAndFees.preTaxPrice = getPreTaxPrice(response.data)

                response.data.customer.firstName = converToLowerCase(response.data.customer.firstName)
                response.data.customer.fullName = getFullName(response.data.customer)
                response.data.customer.fullAddress = getFullAddress(response.data.customer)

                response.data.transactionType = getTransactionType(response.data)

                if (response.data?.federalEVTaxCredit || response.data?.stateEVTaxCredit) {
                    response.data.taxesAndFees.federalEVTaxCredit = response.data?.federalEVTaxCredit
                        ? response.data?.federalEVTaxCredit
                        : response.data?.stateEVTaxCredit
                }

                response.data.surfaceProtection = getSurfaceProtection(response.data?.dealerProducts)

                if (response.data.pdsDealerSellingPrice) {
                    response.data.pdsDealerSellingPrice.dealerDiscount = getDealerDiscount(
                        response.data.pdsDealerSellingPrice.dealerDiscount
                    )
                    response.data.pdsDealerSellingPrice.totalAXZPlanPricing = response?.data?.totalAXZPlanPricing
                    delete response.data.pdsDealerSellingPrice.vehicleSellingPrice
                }
            }
        },
        onError: () => {
            errorHandler()
        }
    })
}

export const useUpdateDealStatus = () => {
    return useMutation(updateDealStatus, {
        onSuccess: (data, status) => {
            const { setDynamicManageData } = useDynamicManageStore.getState()
            setDynamicManageData({
                pdsStatus: status
            })
        },
        onError: () => {
            errorHandler()
        }
    })
}
